/**
 * Print Formats Page
 * Read-only list of all custom print formats, grouped by DocType.
 * Replaces the generic Frappe Print Format list which doesn't show Jinja formats properly.
 */

import { useEffect, useState } from "react";
import { Printer, FileText, Loader2 } from "lucide-react";
import { getPrintConfig } from "@/lib/print/printService";
import { cn } from "@/lib/utils";

type FormatEntry = { name: string; label: string };

type DocTypeGroup = {
  doctype: string;
  doctypeLabel: string;
  icon: typeof FileText;
  formats: FormatEntry[];
  loading: boolean;
};

/** DocTypes that have custom print formats */
const PRINT_DOCTYPES: Array<{
  doctype: string;
  label: string;
  icon: typeof FileText;
}> = [
  { doctype: "Quotation", label: "ใบเสนอราคา (Quotation)", icon: FileText },
  { doctype: "Sales Order", label: "คำสั่งขาย (Sales Order)", icon: Printer },
];

export function PrintFormatsPage() {
  const [groups, setGroups] = useState<DocTypeGroup[]>(
    PRINT_DOCTYPES.map((d) => ({
      doctype: d.doctype,
      doctypeLabel: d.label,
      icon: d.icon,
      formats: [],
      loading: true,
    }))
  );

  useEffect(() => {
    PRINT_DOCTYPES.forEach((dt, idx) => {
      getPrintConfig(dt.doctype)
        .then((config) => {
          setGroups((prev) => {
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              formats: config.formats || [],
              loading: false,
            };
            return next;
          });
        })
        .catch(() => {
          setGroups((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], formats: [], loading: false };
            return next;
          });
        });
    });
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-lg font-semibold text-foreground">
          รูปแบบการพิมพ์
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Print Formats — รายการรูปแบบการพิมพ์ที่ใช้งานได้ในระบบ
        </p>
      </div>

      {/* Format groups */}
      <div className="flex flex-col gap-6">
        {groups.map((group) => (
          <div
            key={group.doctype}
            className="rounded-xl border border-border/40 bg-card shadow-sm"
          >
            {/* Group header */}
            <div className="flex items-center gap-3 border-b border-border/40 px-5 py-3.5">
              <group.icon className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-medium text-foreground">
                {group.doctypeLabel}
              </h2>
              {!group.loading && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {group.formats.length} รูปแบบ
                </span>
              )}
            </div>

            {/* Format rows */}
            <div className="divide-y divide-border/30">
              {group.loading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  กำลังโหลด...
                </div>
              ) : group.formats.length === 0 ? (
                <div className="px-5 py-6 text-center text-sm text-muted-foreground">
                  ไม่มีรูปแบบการพิมพ์
                </div>
              ) : (
                group.formats.map((fmt) => (
                  <FormatRow
                    key={fmt.name}
                    format={fmt}
                    doctype={group.doctype}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormatRow({
  format,
  doctype: _doctype,
}: {
  format: FormatEntry;
  doctype: string;
}) {
  const isStandard = format.name === "Standard";

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/30",
      )}
    >
      <Printer className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-foreground">{format.label}</span>
        {isStandard && (
          <span className="ml-2 text-xs text-muted-foreground">(ค่าเริ่มต้น)</span>
        )}
      </div>
      {!isStandard && (
        <span className="text-xs text-muted-foreground">Custom</span>
      )}
    </div>
  );
}
